import React from 'react';

const ActivityList = ({ activities, filters, onActivitySelect, onActivityDelete, onFilterChange, onAddActivity }) => {
  const activityCategories = {
    '學術': { color: 'blue', icon: '📚' },
    '體育': { color: 'green', icon: '🏃' },
    '藝術': { color: 'purple', icon: '🎨' },
    '社會服務': { color: 'yellow', icon: '🤝' },
    '領袖訓練': { color: 'red', icon: '👑' },
    '考試': { color: 'darkred', icon: '📝' },
    '假期': { color: 'darkgreen', icon: '🎊' },
    '典禮': { color: 'darkblue', icon: '🏛️' },
    '競賽': { color: 'orange', icon: '🏆' }
  };

  const getCategoryColor = (category) => {
    return activityCategories[category]?.color || 'gray';
  };

  const getCategoryIcon = (category) => {
    return activityCategories[category]?.icon || '📋';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayNames = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const dayName = dayNames[date.getDay()];
    
    return `${year}年${month}月${day}日 (${dayName})`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const getTotalParticipations = () => {
    return activities.reduce((sum, activity) => sum + (activity.participant_count || 0), 0);
  };

  return (
    <div className="activity-list">
      <div className="activity-list-header">
        <h2>活動列表</h2>
        <div className="activity-stats">
          <div className="stat-item">
            <span className="stat-number">{activities.length}</span>
            <span className="stat-label">總活動數</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{getTotalParticipations()}</span>
            <span className="stat-label">參與人次</span>
          </div>
        </div>
      </div>

      <div className="activity-filters">
        <div className="filter-group">
          <label>類別篩選:</label>
          <select 
            value={filters.category} 
            onChange={(e) => onFilterChange({ category: e.target.value })}
            className="filter-select"
          >
            <option value="all">全部類別</option>
            {Object.keys(activityCategories).map(category => (
              <option key={category} value={category}>
                {activityCategories[category].icon} {category}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>月份篩選:</label>
          <select 
            value={filters.month} 
            onChange={(e) => onFilterChange({ month: e.target.value })}
            className="filter-select"
          >
            <option value="all">全部月份</option>
            {['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'].map((month, index) => (
              <option key={index} value={index + 1}>{month}</option>
            ))}
          </select>
        </div>

        <button 
          className="add-activity-btn"
          onClick={onAddActivity}
        >
          ➕ 新增活動
        </button>
      </div>

      <div className="activities-grid">
        {activities.map((activity) => (
          <div key={activity.id} className="activity-card">
            <div className="activity-card-header">
              <div className="activity-category">
                <span className={`category-icon ${getCategoryColor(activity.activity_category)}`}>
                  {getCategoryIcon(activity.activity_category)}
                </span>
                <span className={`category-label ${getCategoryColor(activity.activity_category)}`}>
                  {activity.activity_category}
                </span>
              </div>
              <div className="activity-duration">
                {activity.duration_hours}小時
              </div>
            </div>

            <div className="activity-content">
              <h3 className="activity-title">{activity.activity_name}</h3>
              {activity.activity_name_en && (
                <h4 className="activity-title-en">{activity.activity_name_en}</h4>
              )}
              
              <div className="activity-details">
                <div className="detail-item">
                  <span className="detail-icon">📅</span>
                  <span className="detail-text">{formatDate(activity.activity_date)}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">⏰</span>
                  <span className="detail-text">
                    {formatTime(activity.start_time)} - {formatTime(activity.end_time)}
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">📍</span>
                  <span className="detail-text">{activity.location}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">🏢</span>
                  <span className="detail-text">{activity.organizer}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">👥</span>
                  <span className="detail-text">{activity.participant_count}人參與</span>
                </div>
              </div>

              {activity.activity_description && (
                <div className="activity-description">
                  <p>{activity.activity_description}</p>
                </div>
              )}

              <div className="activity-tags">
                <span className={`tag category-tag ${getCategoryColor(activity.activity_category)}`}>
                  {activity.activity_category}
                </span>
                <span className="tag duration-tag">
                  {activity.duration_hours}小時
                </span>
                {activity.target_grades && activity.target_grades.length > 0 && (
                  <span className="tag grade-tag">
                    中{activity.target_grades.join('、')}
                  </span>
                )}
              </div>
            </div>

            <div className="activity-actions">
              <button 
                className="action-btn edit-btn"
                onClick={() => onActivitySelect(activity)}
              >
                編輯
              </button>
              <button 
                className="action-btn details-btn"
                onClick={() => onActivitySelect(activity)}
              >
                詳情
              </button>
              <button 
                className="action-btn delete-btn"
                onClick={() => {
                  if (window.confirm('確定要刪除這個活動嗎？')) {
                    onActivityDelete(activity.id);
                  }
                }}
              >
                刪除
              </button>
            </div>
          </div>
        ))}
      </div>

      {activities.length === 0 && (
        <div className="empty-activities">
          <div className="empty-icon">📋</div>
          <h3>暫無活動資料</h3>
          <p>目前沒有符合篩選條件的活動</p>
          <button 
            className="add-activity-btn"
            onClick={onAddActivity}
          >
            ➕ 新增第一個活動
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityList;
